import React, { useMemo, useState } from 'react';
import { PAGE_SIZE_OPTIONS } from '@constants/pagination';
import { Table, Pagination, Spin, Empty } from 'antd';
import PageLayout from '@components/layout/PageLayout';
import { useOattAuditData } from '@hooks/graphql/useOattAuditData';
import OattAuditFilterPanel, { OattAuditFilters, getOattDateRange } from './OattAuditFilterPanel';
import { buildAuditColumns, ExpandedRow } from '@pages/audit/auditTableConfig';
import styles from './OattAudit.module.css';

const initialDateRange = getOattDateRange('thisMonth');

const OattAudit: React.FC = () => {
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<OattAuditFilters>({
    ...initialDateRange,
    customerId: null,
  });

  const { data, totalCount, loading, currentPage, pageSize, onChangePage } = useOattAuditData(
    filters.startDate || undefined,
    filters.endDate || undefined,
    filters.customerId ?? null,
    filters.suppressQuery,
  );

  const toggleExpand = (id: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const columns = buildAuditColumns(expandedKeys, toggleExpand);

  const emptyDescription = useMemo(() => {
    if (filters.customerId) {
      return (
        <span>
          No audit activity found for this customer in the selected time frame.
          <br />
          Try a wider range — e.g. switch from Last 7 Days to Last 3 Months.
        </span>
      );
    }
    return 'No audit events';
  }, [filters.customerId]);

  return (
    <PageLayout title="OATT Audit Trail">
      <OattAuditFilterPanel recordCount={data.length} onChange={setFilters} />
      <div className={styles.tableWrapper}>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            pagination={false}
            size="middle"
            locale={{ emptyText: <Empty description={emptyDescription} /> }}
            expandable={{
              expandedRowKeys: Array.from(expandedKeys),
              expandedRowRender: (record) => <ExpandedRow record={record} />,
              rowExpandable: (record) => record.before !== null || record.after !== null,
              showExpandColumn: false,
            }}
          />
        </Spin>

        {totalCount > 0 && (
          <div className={styles.paginationBar}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalCount}
              onChange={onChangePage}
              showSizeChanger
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              showTotal={(total) => `${total} records`}
            />
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default OattAudit;
